import React, { useState } from 'react';
import {
    Container,
    Paper,
    Stack,
    Text,
    Group,
    Title,
    Button,
    Alert,
    ThemeIcon,
    Box,
    Badge,
    Divider
} from '@mantine/core';
import {
    IconRefresh,
    IconUsers,
    IconBuilding,
    IconCheck,
    IconX,
    IconSparkles,
    IconDashboard
} from '@tabler/icons-react';
import useAuthenticatedPost from '../../hooks/useAuthenticatedPost';
import config from '../../config';

const ScorecardAdmin = () => {
    const [isDepartmentRefreshing, setIsDepartmentRefreshing] = useState(false);
    const [isIndividualRefreshing, setIsIndividualRefreshing] = useState(false);
    const [departmentResult, setDepartmentResult] = useState(null);
    const [individualResult, setIndividualResult] = useState(null);

    const { post } = useAuthenticatedPost();

    const handleDepartmentRefresh = async () => {
        setIsDepartmentRefreshing(true);
        setDepartmentResult(null);
        
        try {
            const result = await post(
                `${config.apiBaseUrl}/supply-chain/repairs/department/scorecard/refresh`,
                {}
            );
            
            setDepartmentResult({
                hasError: result.hasError,
                message: result.message || 'Department scorecards refreshed successfully!'
            });
            
            // Clear result after 5 seconds
            setTimeout(() => setDepartmentResult(null), 5000);
        } catch (err) {
            console.error('Department refresh error:', err);
            setDepartmentResult({ 
                hasError: true, 
                message: err.message || 'Failed to refresh department scorecards' 
            });
        } finally {
            setIsDepartmentRefreshing(false);
        }
    };

    const handleIndividualRefresh = async () => {
        setIsIndividualRefreshing(true);
        setIndividualResult(null);
        
        try {
            const result = await post(
                `${config.apiBaseUrl}/supply-chain/repairs/individual/scorecard/refresh`,
                {}
            );
            
            setIndividualResult({
                hasError: result.hasError,
                message: result.message || 'Individual scorecards refreshed successfully!'
            });
            
            // Clear result after 5 seconds
            setTimeout(() => setIndividualResult(null), 5000);
        } catch (err) {
            console.error('Individual refresh error:', err);
            setIndividualResult({ 
                hasError: true, 
                message: err.message || 'Failed to refresh individual scorecards' 
            });
        } finally {
            setIsIndividualRefreshing(false);
        }
    };

    return (
        <Container size="lg" py="md">
            <Stack gap="md">
                {/* Header Section */}
                <Paper p="md" withBorder>
                    <Group justify="space-between" align="center">
                        <Group gap="xs">
                            <IconDashboard size={28} />
                            <Title order={2}>Scorecard Administration</Title>
                        </Group>
                        <Badge color="grape" variant="light" size="lg">
                            Admin Tools
                        </Badge>
                    </Group>
                </Paper>

                {/* Department Scorecards Section */}
                <Paper p="xl" withBorder>
                    <Stack gap="md">
                        <Group gap="xs">
                            <ThemeIcon color="blue" size="lg" radius="md">
                                <IconBuilding size={20} />
                            </ThemeIcon>
                            <Title order={3}>Department Scorecards</Title>
                        </Group>
                        
                        <Text size="md" c="dimmed">
                            Refresh all department-level scorecards to update metrics, KPIs, and performance indicators 
                            across all organizational departments. This process will recalculate all aggregated data and 
                            update visualizations.
                        </Text>

                        <Box>
                            <Text size="sm" fw={500} mb="xs">What this does:</Text>
                            <Stack gap={4}>
                                <Text size="sm" c="dimmed">• Recalculates department-wide metrics and KPIs</Text>
                                <Text size="sm" c="dimmed">• Updates performance trends and historical data</Text>
                                <Text size="sm" c="dimmed">• Refreshes department comparison rankings</Text>
                                <Text size="sm" c="dimmed">• Synchronizes with latest data sources</Text>
                            </Stack>
                        </Box>

                        <Divider />

                        <Group justify="space-between" align="center">
                            <Button
                                size="md"
                                leftSection={<IconRefresh size={18} />}
                                onClick={handleDepartmentRefresh}
                                loading={isDepartmentRefreshing}
                                color={departmentResult?.hasError === false ? 'green' : 'blue'}
                                variant="filled"
                            >
                                {isDepartmentRefreshing ? 'Refreshing...' : 'Refresh Department Scorecards'}
                            </Button>
                            
                            {departmentResult && (
                                <Badge
                                    color={departmentResult.hasError ? 'red' : 'green'}
                                    variant="light"
                                    leftSection={departmentResult.hasError ? <IconX size={14} /> : <IconCheck size={14} />}
                                    size="lg"
                                >
                                    {departmentResult.message}
                                </Badge>
                            )}
                        </Group>
                    </Stack>
                </Paper>

                {/* Individual Scorecards Section */}
                <Paper p="xl" withBorder>
                    <Stack gap="md">
                        <Group gap="xs">
                            <ThemeIcon color="teal" size="lg" radius="md">
                                <IconUsers size={20} />
                            </ThemeIcon>
                            <Title order={3}>Individual Scorecards</Title>
                        </Group>
                        
                        <Text size="md" c="dimmed">
                            Refresh all individual employee scorecards to update personal performance metrics, goals, 
                            and achievements. This ensures all team members have the most current performance data 
                            and feedback.
                        </Text>

                        <Box>
                            <Text size="sm" fw={500} mb="xs">What this does:</Text>
                            <Stack gap={4}>
                                <Text size="sm" c="dimmed">• Updates individual performance metrics</Text>
                                <Text size="sm" c="dimmed">• Recalculates goal completion percentages</Text>
                                <Text size="sm" c="dimmed">• Refreshes peer comparison data</Text>
                                <Text size="sm" c="dimmed">• Syncs with HR and project management systems</Text>
                            </Stack>
                        </Box>

                        <Divider />

                        <Group justify="space-between" align="center">
                            <Button
                                size="md"
                                leftSection={<IconRefresh size={18} />}
                                onClick={handleIndividualRefresh}
                                loading={isIndividualRefreshing}
                                color={individualResult?.hasError === false ? 'green' : 'teal'}
                                variant="filled"
                            >
                                {isIndividualRefreshing ? 'Refreshing...' : 'Refresh Individual Scorecards'}
                            </Button>
                            
                            {individualResult && (
                                <Badge
                                    color={individualResult.hasError ? 'red' : 'green'}
                                    variant="light"
                                    leftSection={individualResult.hasError ? <IconX size={14} /> : <IconCheck size={14} />}
                                    size="lg"
                                >
                                    {individualResult.message}
                                </Badge>
                            )}
                        </Group>
                    </Stack>
                </Paper>

                {/* Info Section */}
                <Paper p="md" withBorder  >
                    <Group gap="xs">
                        <ThemeIcon color="yellow" size="md" radius="xl" variant="light">
                            <IconSparkles size={16} />
                        </ThemeIcon>
                        <Text size="sm" fw={500}>Pro Tip:</Text>
                        <Text size="sm" c="dimmed">
                            Refresh operations may take several minutes depending on data volume. 
                            It's recommended to run these during off-peak hours for optimal performance.
                        </Text>
                    </Group>
                </Paper>
            </Stack>
        </Container>
    );
};

export default ScorecardAdmin;